import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("scenarios.urlEmbedCheck");
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

export type UrlEmbedVerdict = "ok" | "blocked" | "unreachable" | "unknown";

export type UrlEmbedResult = {
  verdict: UrlEmbedVerdict;
  url: string;
  status?: number;
  content_type?: string;
  reason?: string;
};

/** Mirror the server-side normalisation so cache keys and iframes agree. */
export function normalizeDisplayUrl(raw: string): string {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const cache = new Map<string, Promise<UrlEmbedResult>>();
/** Synchronously readable mirror of resolved probes (for form validation). */
const settled = new Map<string, UrlEmbedResult>();

/** Verdicts that must block a trainer from using a URL as a learner display. */
export function isDisplayableVerdict(v: UrlEmbedVerdict): boolean {
  // "unknown" means our own check was unavailable — don't punish the trainer.
  return v === "ok" || v === "unknown";
}

/** Last known verdict for a URL, if it was already probed this page load. */
export function getCachedUrlEmbedResult(raw: string): UrlEmbedResult | undefined {
  return settled.get(normalizeDisplayUrl(raw));
}

async function request(url: string): Promise<UrlEmbedResult> {
  const correlationId = nextCorrelationId();
  const res = await fetch(
    appendOp(`${SUPABASE_URL}/functions/v1/url-embed-check`, "embed-check"),
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: SUPABASE_KEY,
        authorization: `Bearer ${SUPABASE_KEY}`,
        [CORRELATION_HEADER]: correlationId,
      },
      body: JSON.stringify({ url }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as Partial<UrlEmbedResult>;
  if (!res.ok || !body.verdict) {
    logger.warn("embed check failed", { url, status: res.status, correlationId });
    return { verdict: "unknown", url, reason: "Check unavailable" };
  }
  return { ...body, verdict: body.verdict, url: body.url ?? url } as UrlEmbedResult;
}

/**
 * Ask the backend whether `url` can be framed. Results are cached per
 * normalised URL for the lifetime of the page so re-renders, the picker and
 * the preview modal share one probe.
 */
export function checkUrlEmbeddable(raw: string): Promise<UrlEmbedResult> {
  const url = normalizeDisplayUrl(raw);
  if (!url) {
    return Promise.resolve({ verdict: "unreachable", url, reason: "Empty URL" });
  }
  const hit = cache.get(url);
  if (hit) return hit;
  const p = request(url).catch((e: Error) => {
    cache.delete(url);
    logger.warn("embed check network error", { url, error: e.message });
    return { verdict: "unknown" as UrlEmbedVerdict, url, reason: "Check unavailable" };
  }).then((r) => {
    settled.set(url, r);
    return r;
  });
  cache.set(url, p);
  return p;
}

export function clearUrlEmbedCache(url?: string) {
  if (url) {
    const key = normalizeDisplayUrl(url);
    cache.delete(key);
    settled.delete(key);
  } else {
    cache.clear();
    settled.clear();
  }
}